
/**
 * Extracts latitude and longitude from a parcel's location string.
 * Handles both POINT and POLYGON formats.
 */
export const extractCoordinates = (location: string | null): { latitude?: number; longitude?: number } => {
  if (!location) return {};
  
  let latitude, longitude;

  if (typeof location === 'string') {
    try {
      // Extract coordinates from a POINT
      const pointMatch = location.match(/POINT\(([^ ]+) ([^ ]+)\)/);
      if (pointMatch) {
        longitude = parseFloat(pointMatch[1]);
        latitude = parseFloat(pointMatch[2]);
        if (!isNaN(latitude) && !isNaN(longitude)) {
          return { latitude, longitude };
        }
      }
      
      // Extract coordinates from a POLYGON
      const polygonMatch = location.match(/POLYGON\(\((.*)\)\)/);
      if (polygonMatch) {
        const firstCoordPair = polygonMatch[1].split(',')[0].trim().split(' ');
        if (firstCoordPair.length === 2) {
          longitude = parseFloat(firstCoordPair[0]);
          latitude = parseFloat(firstCoordPair[1]);
          if (!isNaN(latitude) && !isNaN(longitude)) {
            return { latitude, longitude };
          }
        }
      }
    } catch (error) {
      console.error("Error extracting coordinates from location string:", error);
    }
  }

  return {};
};

/**
 * Processes a parcel to add latitude, longitude and ensure polygon_coords.
 * Ensures proper typing for polygon_coords as number[][] instead of Json.
 */
export const processParcel = (parcel: any) => {
  try {
    const { latitude, longitude } = extractCoordinates(parcel.location);
    
    // Ensure polygon_coords is correctly formatted as number[][]
    let processedPolygonCoords: number[][] = [];
    
    if (parcel.polygon_coords) {
      // Handle different possible formats of polygon_coords
      if (Array.isArray(parcel.polygon_coords)) {
        // It's already an array, check if it's an array of arrays of numbers
        if (parcel.polygon_coords.every((coords: any) => Array.isArray(coords))) {
          processedPolygonCoords = parcel.polygon_coords;
        }
      } else if (typeof parcel.polygon_coords === 'string') {
        // It's a string, try to parse it
        try {
          const parsed = JSON.parse(parcel.polygon_coords);
          if (Array.isArray(parsed) && parsed.every((coords: any) => Array.isArray(coords))) {
            processedPolygonCoords = parsed;
          }
        } catch (e) {
          console.error("Error parsing polygon_coords:", e);
        }
      } else if (parcel.polygon_coords && typeof parcel.polygon_coords === 'object') {
        // It might be a JSON object from Supabase
        try {
          // If it has a key with coordinates array, try to extract it
          const keys = Object.keys(parcel.polygon_coords);
          if (keys.length > 0 && Array.isArray(parcel.polygon_coords[keys[0]])) {
            processedPolygonCoords = parcel.polygon_coords[keys[0]];
          }
        } catch (e) {
          console.error("Error processing polygon_coords as object:", e);
        }
      }
    }
    
    return {
      ...parcel,
      latitude: parcel.latitude || latitude,
      longitude: parcel.longitude || longitude,
      polygon_coords: processedPolygonCoords
    };
  } catch (error) {
    console.error("Error in processParcel:", error);
    // Return original parcel with empty polygon_coords to prevent errors
    return {
      ...parcel,
      polygon_coords: []
    };
  }
};
